import { useMemo } from 'react';
import { Icon } from './Icon';
import { EMPTY_FILTERS, type ReportFilters, type ReportRow, type SentStatus } from '../types';
import { STATUSES } from '../mocks/columns';

const SENT_OPTIONS: Array<{ value: SentStatus; label: string }> = [
  { value: 'unsent', label: 'Unsent' },
  { value: 'sent', label: 'Sent' },
  { value: 'sent_changed', label: 'Sent — changed since' },
];

interface Props {
  rows: ReportRow[];
  filters: ReportFilters;
  setFilters(f: ReportFilters): void;
  collapsed: boolean;
  onToggle(): void;
}

export function FilterSidebar({ rows, filters, setFilters, collapsed, onToggle }: Props): JSX.Element {
  const accounts = useMemo(
    () => [...new Set(rows.map((r) => r.account).filter((x): x is string => !!x))].sort(),
    [rows],
  );
  const affiliates = useMemo(
    () => [...new Set(rows.map((r) => r.affiliate).filter((x): x is string => !!x))].sort(),
    [rows],
  );

  const toggle = (key: 'account' | 'affiliate' | 'status' | 'sent', v: string) => {
    const set = new Set<string>(filters[key]);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    setFilters({ ...filters, [key]: [...set] });
  };

  const totalFilters =
    filters.account.length + filters.affiliate.length + filters.status.length + filters.sent.length
    + (filters.coachee ? 1 : 0) + (filters.opp ? 1 : 0);

  return (
    <div className={'fsidebar' + (collapsed ? ' collapsed' : '')}>
      <div className="fsidebar-hd">
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="lbl">Filters</span>
            {totalFilters > 0 && (
              <span style={{ background: 'var(--accent)', color: '#fff', fontSize: 10, padding: '1px 6px', borderRadius: 99, fontWeight: 600, letterSpacing: 0.05 }}>
                {totalFilters}
              </span>
            )}
          </div>
        )}
        <div className="collapse-btn" onClick={onToggle} title={collapsed ? 'Expand filters' : 'Collapse filters'}>
          <Icon name={collapsed ? 'sidebarCollapsed' : 'sidebar'} size={15} />
        </div>
      </div>
      <div className="fsidebar-body">
        <div className="fs-group">
          <label>Date incurred</label>
          <div className="fs-row">
            <input className="input" placeholder="From" defaultValue="2026-04-01" />
            <input className="input" placeholder="To" defaultValue="" />
          </div>
        </div>
        <div className="fs-group">
          <label>Account · <span style={{ color: 'var(--mute)' }}>{filters.account.length || 0}/{accounts.length}</span></label>
          <input className="input" placeholder={`Search ${accounts.length} accounts…`} style={{ width: '100%', marginBottom: 6 }} />
          <div className="chk-list">
            {accounts.map((a) => (
              <label key={a} title={a}>
                <input type="checkbox" checked={filters.account.includes(a)} onChange={() => toggle('account', a)} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>Affiliate</label>
          <input className="input" placeholder={`Search ${affiliates.length} affiliates…`} style={{ width: '100%', marginBottom: 6 }} />
          <div className="chk-list">
            {affiliates.slice(0, 6).map((a) => (
              <label key={a}>
                <input type="checkbox" checked={filters.affiliate.includes(a)} onChange={() => toggle('affiliate', a)} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>Coachee</label>
          <input
            className="input"
            placeholder="Search coachees…"
            style={{ width: '100%' }}
            value={filters.coachee}
            onChange={(e) => setFilters({ ...filters, coachee: e.target.value })}
          />
        </div>
        <div className="fs-group">
          <label>Status</label>
          <div className="chk-list" style={{ maxHeight: 'none' }}>
            {STATUSES.map((s) => (
              <label key={s}>
                <input type="checkbox" checked={filters.status.includes(s)} onChange={() => toggle('status', s)} />
                <span>{s}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>Sent to finance</label>
          <div className="chk-list" style={{ maxHeight: 'none' }}>
            {SENT_OPTIONS.map((o) => (
              <label key={o.value}>
                <input
                  type="checkbox"
                  checked={filters.sent.includes(o.value)}
                  onChange={() => toggle('sent', o.value)}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>NetSuite opportunity #</label>
          <input
            className="input"
            placeholder="O14405"
            style={{ width: '100%' }}
            value={filters.opp}
            onChange={(e) => setFilters({ ...filters, opp: e.target.value })}
          />
        </div>
      </div>
      <div className="fsidebar-foot">
        <a className="btn-link" onClick={() => setFilters(EMPTY_FILTERS)}>Reset all filters</a>
      </div>
    </div>
  );
}
